// Custom events are dispatched by the theme core. This file intentionally
// does not register empty listeners for those events; consumers can subscribe
// directly when they need cart/product/quick-view notifications.

// Artist filter letter browser: letters are independent multi-select toggles.
// Selected letters are kept here (in JS memory), not read from the DOM,
// specifically because Shopify's native filter update replaces this entire
// block of HTML from the server on every checkbox change — anything stored
// only in the DOM (like which letters were active) would be silently wiped
// out and reset to the default every time, which is exactly the "completely
// refreshes and re-shows everything" bug this fixes.
//
// State is keyed per section (via the nearest [data-section-id]) rather than
// a single global array, so two artist filters on the same page (e.g. a
// collection section and a related-artists block) can't cross-contaminate
// each other's selections.
var artistLetterState = Object.create(null);

function getArtistFilterSectionKey(el) {
  var section = el.closest("[data-section-id]");
  return section ? section.getAttribute("data-section-id") : "global";
}

function getSelectedArtistLetters(key) {
  return artistLetterState[key] || (artistLetterState[key] = []);
}

// Scoped to a single lettersWrap instance. Keeping this scoped (rather than
// looping over the whole document on every call) is what lets the
// MutationObserver below re-apply state to just the section that changed.
function applyArtistLetterStateFor(lettersWrap) {
  var listEl = lettersWrap.nextElementSibling;
  if (!listEl || !listEl.hasAttribute("data-artist-filter-list")) return;

  var key = getArtistFilterSectionKey(lettersWrap);
  var selectedLetters = getSelectedArtistLetters(key);

  // A letter that was selected before a re-render may no longer be a valid,
  // available bucket after the server re-renders with new filter counts
  // (e.g. the last artist in "Q" gets filtered out by another active
  // filter). Intersecting against the buttons actually rendered as enabled
  // keeps the in-memory state honest instead of silently filtering the list
  // by a letter that's no longer selectable in the UI.
  var availableLetters = [];
  lettersWrap.querySelectorAll("[data-artist-filter-letter]").forEach(function (btn) {
    var letter = btn.getAttribute("data-artist-filter-letter");
    if (letter !== "all" && !btn.disabled) {
      availableLetters.push(letter);
    }
  });
  selectedLetters = selectedLetters.filter(function (letter) {
    return availableLetters.indexOf(letter) !== -1;
  });
  artistLetterState[key] = selectedLetters;

  lettersWrap.querySelectorAll("[data-artist-filter-letter]").forEach(function (btn) {
    var letter = btn.getAttribute("data-artist-filter-letter");
    var isActive =
      letter === "all"
        ? selectedLetters.length === 0
        : selectedLetters.indexOf(letter) !== -1;
    btn.classList.toggle("is-active", isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });

  var anyChecked = false;

  listEl.querySelectorAll("[data-first-letter]").forEach(function (item) {
    var input = item.querySelector("input");
    var isChecked = Boolean(input && input.checked);
    if (isChecked) anyChecked = true;

    var matchesLetter = selectedLetters.indexOf(item.getAttribute("data-first-letter")) !== -1;
    item.hidden = !(matchesLetter || isChecked);
  });

  listEl.hidden = selectedLetters.length === 0 && !anyChecked;
}

function applyArtistLetterState(root) {
  var scope = root || document;
  scope.querySelectorAll("[data-artist-filter-letters]").forEach(applyArtistLetterStateFor);
  // `scope` itself may *be* the letters wrap when called from the
  // per-section MutationObserver below.
  if (scope.matches && scope.matches("[data-artist-filter-letters]")) {
    applyArtistLetterStateFor(scope);
  }
}

document.addEventListener("click", function (event) {
  var letterBtn = event.target.closest("[data-artist-filter-letter]");
  if (!letterBtn || letterBtn.disabled) return;

  var lettersWrap = letterBtn.closest("[data-artist-filter-letters]");
  if (!lettersWrap) return;

  var key = getArtistFilterSectionKey(lettersWrap);
  var selectedLetters = getSelectedArtistLetters(key);
  var letter = letterBtn.getAttribute("data-artist-filter-letter");

  if (letter === "all") {
    selectedLetters.length = 0;
  } else {
    var index = selectedLetters.indexOf(letter);
    if (index === -1) {
      selectedLetters.push(letter);
    } else {
      selectedLetters.splice(index, 1);
    }
  }

  applyArtistLetterStateFor(lettersWrap);
});

// Filter groups with only one inactive available option are hidden by
// filter-group-visible.liquid. Never auto-select a value on a visitor's behalf.

// Re-apply the persisted artist-letter state, whenever this markup
// gets freshly re-rendered by the theme's native AJAX filter update (which
// swaps in brand-new DOM nodes that know nothing about either).
//
// Observing each collection/search section individually (rather than
// document.body) means this never fires for unrelated churn elsewhere on
// the page - cart drawers, recommendation carousels, analytics-injected
// nodes, etc.
function observeFilterSection(section) {
  var observer = new MutationObserver(function (mutations) {
    var relevant = false;

    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (
          node.matches &&
          (node.matches("[data-artist-filter-letters], [data-filter-type]") ||
            node.querySelector("[data-artist-filter-letters], [data-filter-type]"))
        ) {
          relevant = true;
        }
      });
    });

    if (relevant) {
      applyArtistLetterState(section);
    }
  });

  observer.observe(section, { childList: true, subtree: true });
  return observer;
}

var observedArtistFilterSections = new WeakSet();

function maybeObserveFilterSection(section) {
  if (!section || observedArtistFilterSections.has(section)) return;
  if (!section.querySelector("[data-artist-filter-letters], [data-artist-filter-list], [data-filter-type]")) return;
  observeFilterSection(section);
  observedArtistFilterSections.add(section);
}

document.querySelectorAll("[data-section-id]").forEach(maybeObserveFilterSection);

// Theme Editor can replace a section without a full navigation. Only attach a
// new observer when the reloaded section actually contains artist filters.
document.addEventListener("shopify:section:load", function (event) {
  var section = event.target && event.target.querySelector
    ? event.target.querySelector("[data-section-id]") || event.target.closest && event.target.closest("[data-section-id]")
    : null;
  if (section) maybeObserveFilterSection(section);
});

// Browser Back/Forward restores a server-rendered filter state that this
// in-memory letter selection knows nothing about. Without this, the letter
// browser could keep showing a stale selection that no longer matches the
// filters actually applied after navigating back.
window.addEventListener("popstate", function () {
  artistLetterState = Object.create(null);
});

applyArtistLetterState();

// Retired Artist-name sort URL cleanup ---------------------------------------
// The Artist name A–Z/Z–A options are temporarily hidden because Shopify does
// not natively paginate collection results by the artwork Artist metafield.
// Preserve every active filter/query parameter, remove only the retired sort
// value and page number, then reload the same collection in its default order.
(function removeRetiredArtistSortParameter() {
  var url = new URL(window.location.href);
  var sortValue = url.searchParams.get('sort_by');

  if (sortValue !== 'artist-ascending' && sortValue !== 'artist-descending') return;

  url.searchParams.delete('sort_by');
  url.searchParams.delete('page');

  window.location.replace(url.pathname + url.search + url.hash);
})();

// Complete collection dataset orchestration -----------------------------------
// Series de-duplication and Artist-name sorting operate on the complete
// filtered collection, rather than only on the currently visible Shopify page.
// Requests are cancellable, concurrency-limited and cached per filtered URL.
(function initialiseCompleteCollectionDataset() {
  // This orchestration only applies to collection product grids. custom-events
  // also loads on search pages, so leave those pages with zero dataset listeners,
  // observers or timers.
  if (!document.querySelector('.collection__products')) return;

  var requestToken = 0;
  var activeController = null;
  var datasetCache = new Map();
  var rebuildTimer = null;
  var MAX_PAGES = 250;
  var MAX_CONCURRENT_REQUESTS = 4;

  function currentSeriesProductIds() {
    var ids = Object.create(null);
    document.querySelectorAll('[data-series-product-ids]').forEach(function (slider) {
      String(slider.getAttribute('data-series-product-ids') || '')
        .split('|')
        .forEach(function (id) {
          if (id) ids[id] = true;
        });
    });
    return ids;
  }

  function requestedArtistSort() {
    var value = new URL(window.location.href).searchParams.get('sort_by');
    return value === 'artist-ascending' || value === 'artist-descending' ? value : '';
  }

  function canonicalDatasetUrl(url) {
    var next = new URL(url, window.location.origin);
    next.searchParams.delete('page');
    next.searchParams.delete('section_id');
    if (next.searchParams.get('sort_by') === 'artist-ascending' || next.searchParams.get('sort_by') === 'artist-descending') {
      next.searchParams.delete('sort_by');
    }
    return next;
  }

  function pageUrl(baseUrl, page) {
    var next = new URL(baseUrl.toString());
    next.searchParams.set('page', String(page));
    return next.toString();
  }

  function cardKey(card) {
    return card.getAttribute('data-product-id') ||
      (card.querySelector('[data-product-id]') && card.querySelector('[data-product-id]').getAttribute('data-product-id')) ||
      '';
  }

  function cardArtist(card) {
    return String(card.getAttribute('data-product-artist') || '').trim();
  }

  function cardTitle(card) {
    var title = card.querySelector('[data-product-title], .product-item__product-title, .product-item__title');
    return String(title ? title.textContent : card.textContent || '').trim();
  }

  function announce(message) {
    var region = document.querySelector('[data-results-count][aria-live], [data-filter-results-live-region]');
    if (!region) return;
    region.setAttribute('aria-atomic', 'true');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('data-dataset-status', message);
  }

  function parsePositiveInteger(value) {
    var parsed = parseInt(String(value == null ? '' : value).replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function parseNumberedPageCount(doc) {
    var maxPage = 1;
    doc.querySelectorAll('.pagination a[href], .pagination [data-page]').forEach(function (link) {
      var href = link.getAttribute('href');
      var explicit = parsePositiveInteger(link.getAttribute('data-page'));
      if (explicit) maxPage = Math.max(maxPage, explicit);
      if (!href) return;
      try {
        var page = parsePositiveInteger(new URL(href, window.location.origin).searchParams.get('page')) || 1;
        maxPage = Math.max(maxPage, page);
      } catch (error) {
        // Ignore malformed pagination URLs and keep the best known page count.
      }
    });
    return maxPage;
  }

  function parseDatasetPagination(doc, firstPageCardCount) {
    var partial = doc.querySelector('[data-collection-partial], [data-partial]');
    var section = doc.querySelector('[data-section-type="collection"]');
    var total = parsePositiveInteger(
      (partial && partial.getAttribute('data-collection-products-count')) ||
      (section && section.getAttribute('data-collection-products-count'))
    );
    var pageSize = parsePositiveInteger(
      (partial && partial.getAttribute('data-products-per-page')) ||
      (section && section.getAttribute('data-products-per-page'))
    ) || firstPageCardCount;
    var pageCount = total && pageSize
      ? Math.ceil(total / pageSize)
      : parseNumberedPageCount(doc);

    if (pageCount > MAX_PAGES) {
      throw new Error('Complete collection dataset requires ' + pageCount + ' pages, exceeding the safe limit of ' + MAX_PAGES + '.');
    }

    return {
      total: total,
      pageSize: pageSize,
      pageCount: Math.max(1, pageCount)
    };
  }

  function extractCards(doc) {
    return Array.prototype.slice.call(doc.querySelectorAll('.collection__products .product-item'));
  }

  async function fetchDocument(url, signal) {
    var response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: signal
    });
    if (!response.ok) throw new Error('Unable to load complete collection dataset (' + response.status + ')');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async function mapWithConcurrency(items, concurrency, worker) {
    var results = new Array(items.length);
    var nextIndex = 0;
    async function runner() {
      while (nextIndex < items.length) {
        var index = nextIndex++;
        results[index] = await worker(items[index], index);
      }
    }
    var runners = [];
    for (var i = 0; i < Math.min(concurrency, items.length); i += 1) runners.push(runner());
    await Promise.all(runners);
    return results;
  }

  async function collectAllCards(token, signal) {
    var baseUrl = canonicalDatasetUrl(window.location.href);
    var cacheKey = baseUrl.toString();
    if (datasetCache.has(cacheKey)) {
      return datasetCache.get(cacheKey).map(function (card) { return card.cloneNode(true); });
    }

    var firstDoc = await fetchDocument(pageUrl(baseUrl, 1), signal);
    if (token !== requestToken) return [];
    var cards = extractCards(firstDoc);
    var pagination = parseDatasetPagination(firstDoc, cards.length);

    if (pagination.pageCount > 1) {
      var pages = [];
      for (var page = 2; page <= pagination.pageCount; page += 1) pages.push(page);
      var remaining = await mapWithConcurrency(pages, MAX_CONCURRENT_REQUESTS, async function (pageNumber) {
        var doc = await fetchDocument(pageUrl(baseUrl, pageNumber), signal);
        if (token !== requestToken) return [];
        return extractCards(doc);
      });
      remaining.forEach(function (pageCards) { cards.push.apply(cards, pageCards); });
    }

    // Validate the raw server dataset before exclusions, de-duplication or
    // client-side sorting. Never replace the live grid with a partial fetch.
    if (pagination.total > 0 && cards.length < pagination.total) {
      throw new Error(
        'Incomplete collection dataset: expected ' + pagination.total +
        ' artworks but received ' + cards.length + '.'
      );
    }

    datasetCache.set(cacheKey, cards.map(function (card) { return card.cloneNode(true); }));
    return cards;
  }

  function compareCards(a, b, direction) {
    var artistA = cardArtist(a);
    var artistB = cardArtist(b);
    // Missing Artist values always follow named Artists in either direction.
    if (!artistA && artistB) return 1;
    if (artistA && !artistB) return -1;
    var result = artistA.localeCompare(artistB, undefined, { sensitivity: 'base', numeric: true });
    if (direction === 'artist-descending') result *= -1;
    if (result === 0) {
      result = cardTitle(a).localeCompare(cardTitle(b), undefined, { sensitivity: 'base', numeric: true });
    }
    return result;
  }

  async function rebuildCompleteDataset() {
    var grid = document.querySelector('.collection__products');
    if (!grid) return;

    var excluded = currentSeriesProductIds();
    var sortValue = requestedArtistSort();
    if (!Object.keys(excluded).length && !sortValue) return;

    var signatureUrl = canonicalDatasetUrl(window.location.href);
    var signature = signatureUrl.toString() + '|' + sortValue + '|' + Object.keys(excluded).sort().join(',');
    if (grid.getAttribute('data-complete-dataset-signature') === signature) return;

    if (activeController) activeController.abort();
    activeController = new AbortController();
    var token = ++requestToken;
    var section = grid.closest('[data-section-type="collection"], [data-section-id]');
    var previousMinHeight = grid.offsetHeight;
    if (section) section.setAttribute('aria-busy', 'true');
    grid.setAttribute('aria-busy', 'true');
    if (previousMinHeight) grid.style.minHeight = previousMinHeight + 'px';
    announce('Updating artworks');

    try {
      var cards = await collectAllCards(token, activeController.signal);
      if (token !== requestToken || !cards.length) return;

      var seenProducts = Object.create(null);
      cards = cards.filter(function (card) {
        var id = cardKey(card);
        if (id && excluded[id]) return false;
        if (id && seenProducts[id]) return false;
        if (id) seenProducts[id] = true;
        return true;
      });

      if (sortValue) cards.sort(function (a, b) { return compareCards(a, b, sortValue); });

      var fragment = document.createDocumentFragment();
      cards.forEach(function (card) { fragment.appendChild(document.importNode(card, true)); });
      grid.replaceChildren(fragment);
      grid.setAttribute('data-complete-dataset-signature', signature);

      var partial = grid.closest('[data-partial]');
      if (partial) {
        partial.querySelectorAll('.pagination, .collection__infinite-trigger').forEach(function (node) { node.remove(); });
      }
      announce(cards.length + (cards.length === 1 ? ' artwork' : ' artworks'));
      document.dispatchEvent(new CustomEvent('collection:complete-dataset-rendered', { detail: { count: cards.length } }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        announce('Artworks could not be reordered. The original collection remains available.');
      }
    } finally {
      if (token === requestToken) {
        if (section) section.removeAttribute('aria-busy');
        grid.removeAttribute('aria-busy');
        grid.style.minHeight = '';
      }
    }
  }

  function scheduleRebuild() {
    window.clearTimeout(rebuildTimer);
    rebuildTimer = window.setTimeout(rebuildCompleteDataset, 80);
  }

  document.addEventListener('change', function (event) {
    var input = event.target.closest('input[name="sort_by"]');
    if (!input || (input.value !== 'artist-ascending' && input.value !== 'artist-descending')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', input.value);
    url.searchParams.delete('page');
    window.history.pushState({}, '', url.toString());
    scheduleRebuild();
  }, true);

  window.addEventListener('popstate', scheduleRebuild);
  document.addEventListener('DOMContentLoaded', scheduleRebuild);
  document.addEventListener('shopify:section:load', scheduleRebuild);
  if (document.readyState !== 'loading') scheduleRebuild();

  var observerRoot = document.querySelector('.collection__products');
  if (observerRoot) {
    observerRoot = observerRoot.closest('[data-section-type="collection"], [data-section-id]') || observerRoot.parentElement;
  }

  if (observerRoot) {
    var observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && (
            (node.matches && node.matches('.collection__products, [data-series-product-ids]')) ||
            (node.querySelector && node.querySelector('.collection__products, [data-series-product-ids]'))
          );
        });
      });
      if (relevant) scheduleRebuild();
    });

    observer.observe(observerRoot, { childList: true, subtree: true });
  }
})();